import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
  ConversationHeader,
  MessageThread,
  MessageComposer,
  IntakeReviewCard,
  EmailThread,
} from '@/components/messages';
import type { IntakeReviewActionPayload } from '@/components/messages';
import type { TemplateSendPayload } from '@/components/messages/MessageComposer';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { useOpenThreadSync } from '@/hooks/useOpenThreadSync';
import { useWhatsAppTemplates } from '@/hooks/useWhatsAppTemplates';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { requiresTemplate, sessionWindowFromError, windowOpenedBy } from '@/utils/sessionWindow';
import { mergeMessageLists } from '@/utils/mergeMessages';
import { getConversationTitle } from '@/utils/conversationParty';
import { ConversationMediaError, sendConversationMedia } from '@/utils/conversationMedia';
import type { Conversation, Message } from '@/types';

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export default function ConversationPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  // Without WhatsApp only customer portal chats can be answered (#2830).
  const { whatsappAvailable } = useTenantSettings();
  const notify = useNotify();

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesLoadingOlder, setMessagesLoadingOlder] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  // Load conversation and messages
  useEffect(() => {
    if (!customerId) return;

    async function load() {
      setIsLoading(true);
      setError(false);
      try {
        // Try loading by customer ID — the API returns the conversation for this customer
        const convResponse = await api.get<PaginatedResponse<Conversation>>(
          '/conversations',
          { params: { customerId } }
        );
        const conv = convResponse.data[0];
        if (!conv) {
          setError(true);
          setIsLoading(false);
          return;
        }
        setConversation(conv);

        const msgResponse = await api.get<PaginatedResponse<Message>>(
          `/conversations/${conv.id}/messages`
        );
        setMessages(msgResponse.data);
        setMessagesCursor(msgResponse.pagination.nextCursor);
        setMessagesHasMore(msgResponse.pagination.hasMore);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    }
    void load();
  }, [customerId]);

  useOpenThreadSync(conversation?.id ?? null, setMessages);

  const handleLoadOlderMessages = useCallback(async () => {
    if (!conversation || !messagesCursor || messagesLoadingOlder) return;
    setMessagesLoadingOlder(true);
    try {
      const response = await api.get<PaginatedResponse<Message>>(
        `/conversations/${conversation.id}/messages`,
        { params: { cursor: messagesCursor } },
      );
      setMessages((prev) => mergeMessageLists(prev, response.data));
      setMessagesCursor(response.pagination.nextCursor);
      setMessagesHasMore(response.pagination.hasMore);
    } catch {
      notify.error(tc('error'), t('messagesLoadFailed'));
    } finally {
      setMessagesLoadingOlder(false);
    }
  }, [conversation, messagesCursor, messagesLoadingOlder, notify, t, tc]);

  useEffect(() => {
    const lastInbound = [...messages]
      .reverse()
      .find((message) => message.direction === 'inbound' && message.channel === 'whatsapp');
    if (!lastInbound) {
      return;
    }
    setConversation((prev) => {
      if (!prev) {
        return prev;
      }
      const nextWindow = windowOpenedBy(lastInbound.sentAt);
      if (prev.sessionWindow?.lastInboundAt === nextWindow.lastInboundAt) {
        return prev;
      }
      return { ...prev, sessionWindow: nextWindow };
    });
  }, [messages]);

  // Templates are only fetched when a closed 24 h window forces them (#1471).
  const templateMode = conversation ? requiresTemplate(conversation) : false;
  const templatesQuery = useWhatsAppTemplates(templateMode);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!conversation) return;

    try {
      const newMsg = await api.post<Message>(
        `/conversations/${conversation.id}/messages`,
        { content, type: 'text' }
      );
      setMessages((prev) => mergeMessageLists(prev, [newMsg]));
      notify.success(t('messageSent'), t('messageSentDescription'));
    } catch (error) {
      const closedWindow = sessionWindowFromError(error);
      if (closedWindow) {
        setConversation((prev) => (prev ? { ...prev, sessionWindow: closedWindow } : prev));
        notify.error(t('sessionWindowClosed'), t('sessionWindowClosedComposerHint'));
        return;
      }
      notify.error(t('messageSendFailed'));
    }
  }, [conversation, notify, t]);

  const handleSendInternal = useCallback(async (content: string) => {
    if (!conversation) return;
    try {
      const newMsg = await api.post<Message>(
        `/conversations/${conversation.id}/messages`,
        { content, type: 'text', lane: 'internal' }
      );
      setMessages((prev) => mergeMessageLists(prev, [newMsg]));
      notify.success(t('internalNoteSaved'), t('internalNoteSavedDesc'));
    } catch {
      notify.error(t('messageSendFailed'));
    }
  }, [conversation, notify, t]);

  const handleReviewAction = useCallback(async (payload: IntakeReviewActionPayload) => {
    if (!conversation) return;
    setReviewBusy(true);
    try {
      const result = await api.post<{
        staffQueueStatus: string;
        customerPhase: string;
        agentAuthority: string;
        customerMessage?: { id: string; content: string };
      }>(`/conversations/${conversation.id}/staff-decisions`, payload);
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              staffQueueStatus: result.staffQueueStatus,
              customerPhase: result.customerPhase,
              agentAuthority: result.agentAuthority,
            }
          : prev,
      );
      if (result.customerMessage) {
        const relayed = result.customerMessage;
        setMessages((prev) => mergeMessageLists(prev, [{
          id: relayed.id,
          conversationId: conversation.id,
          direction: 'outbound',
          channel: conversation.channel,
          content: relayed.content,
          status: 'sent',
          sentAt: new Date().toISOString(),
        }]));
      }
      const successKey =
        payload.action === 'claim'
          ? 'reviewClaimed'
          : payload.action === 'ask_customer'
            ? 'reviewAsked'
            : payload.action === 'block'
              ? 'reviewBlocked'
              : 'reviewClosed';
      notify.success(t(successKey));
    } catch {
      notify.error(t('reviewActionFailed'));
    } finally {
      setReviewBusy(false);
    }
  }, [conversation, notify, t]);

  const handleSendFile = useCallback(async (file: File, caption: string) => {
    if (!conversation) return;
    try {
      const newMsg = await sendConversationMedia(conversation.id, file, caption);
      setMessages((prev) => mergeMessageLists(prev, [newMsg]));
      notify.success(t('messageSent'), t('messageSentDescription'));
    } catch (error) {
      const closedWindow = sessionWindowFromError(error);
      if (closedWindow) {
        setConversation((prev) => (prev ? { ...prev, sessionWindow: closedWindow } : prev));
        notify.error(t('sessionWindowClosed'), t('sessionWindowClosedComposerHint'));
        return;
      }
      if (error instanceof ConversationMediaError && error.code === 'unsupported') {
        notify.error(t('attachmentUnsupported'));
        return;
      }
      if (error instanceof ConversationMediaError && error.code === 'too_large') {
        notify.error(t('attachmentTooLarge'));
        return;
      }
      notify.error(t('messageSendFailed'));
    }
  }, [conversation, notify, t]);

  const handleSendTemplate = useCallback(async (payload: TemplateSendPayload) => {
    if (!conversation) return;

    try {
      const newMsg = await api.post<Message>(
        `/conversations/${conversation.id}/messages`,
        { type: 'template', ...payload }
      );
      setMessages((prev) => mergeMessageLists(prev, [newMsg]));
      notify.success(t('messageSent'), t('messageSentDescription'));
    } catch {
      notify.error(t('templateSendFailed'));
    }
  }, [conversation, notify, t]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }} role="status" aria-label={tc('loading')}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !conversation) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('noConversations')}</Typography>
      </Box>
    );
  }

  // The customer's newest conversation can be a mailbox email thread (#3736):
  // show it read-only, as on the Messages page. Linking happens there.
  if (conversation.channel === 'email') {
    return (
      <Box sx={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
        <ConversationHeader conversation={conversation} onBack={() => navigate('/messages')} />
        <EmailThread
          messages={messages}
          hasOlder={messagesHasMore}
          loadingOlder={messagesLoadingOlder}
          onLoadOlder={() => { void handleLoadOlderMessages(); }}
        />
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          {t('emailReplyNotYet')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
      <ConversationHeader
        conversation={conversation}
        onBack={() => navigate('/messages')}
      />
      <IntakeReviewCard
        conversation={conversation}
        onAction={handleReviewAction}
        busy={reviewBusy}
      />
      <MessageThread
        messages={messages}
        customerName={getConversationTitle(conversation, t('unknownContact'))}
        typingIndicator={null}
        hasOlder={messagesHasMore}
        loadingOlder={messagesLoadingOlder}
        onLoadOlder={() => { void handleLoadOlderMessages(); }}
      />
      <MessageComposer
        onSend={handleSendMessage}
        onSendInternal={handleSendInternal}
        // The customer portal chat shows text only (#2828).
        {...(conversation.channel === 'portal' ? {} : { onSendFile: handleSendFile })}
        onSendTemplate={handleSendTemplate}
        templateMode={templateMode}
        {...(templatesQuery.data ? { templates: templatesQuery.data } : {})}
        templatesLoading={templatesQuery.isLoading}
        templatesError={templatesQuery.isError}
        {...(!whatsappAvailable && conversation.channel !== 'portal'
          ? { disabled: true, placeholder: t('whatsappNotConnectedComposer') }
          : {})}
      />
    </Box>
  );
}
